/**
 * Student Diary — /diary (teacher, parent, admin) and /learn/diary (the student)
 *
 * One screen, everything on it. Nobody is sent to another module to assess a topic, set
 * homework or reply to a parent — the whole point of the brief.
 *
 *   ┌ date · student · subject ─────────────────────────────────────────┐
 *   │ [tiles: topics covered · teacher accuracy · parent accuracy · HW] │
 *   │ Topics covered today        │  Homework                           │
 *   │   each topic, with BOTH     │  Teacher remarks                    │
 *   │   assessments side by side  │  Parent remarks                     │
 *   │                             │  Parent ↔ teacher messages          │
 *   └───────────────────────────────────────────────────────────────────┘
 *
 * What the caller may do comes from the server (`can`), not from guessing at the role, so
 * the screen and the API can never disagree about permissions.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import RecordPage, { FormSection } from '../../components/shell/RecordPage';
import { StatTiles } from '../../components/shell/Page';
import { useTabLabel } from '../../components/shell/TabsContext';
import AssessmentForm, { AssessmentRecord, accuracyTone } from '../../components/diary/AssessmentForm';
import AssignHomework from '../../components/diary/AssignHomework';
import { PaneFooter } from '../../components/shell/SidePane';
import RichHtml from '../../components/richtext/RichText';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  Notebook, CalendarBlank, Student, BookOpen, Plus, FloppyDisk, Trash, CaretLeft, CaretRight,
  ChatsCircle, ClipboardText, Target, CheckCircle, PaperPlaneTilt, ChalkboardTeacher, UsersThree, Clock,
  ArrowSquareOut,
} from '@phosphor-icons/react';

const today = () => new Date().toISOString().slice(0, 10);
const shiftDate = (iso, days) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
/** Where a homework item leads: its quiz, its topic, or the course it belongs to. */
const homeworkTarget = (h) => {
  if (h.exam_id) return `/learn/exams/${h.exam_id}`;
  if (h.course_id && h.node_id) return `/learn/course/${h.course_id}?node=${h.node_id}`;
  if (h.course_id) return `/learn/course/${h.course_id}`;
  return null;
};

const prettyDate = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso
    : d.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
};
const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const KIND_LABEL = { lesson: 'Lesson', quiz: 'Quiz', theory: 'Theory', qa: 'Q & A', other: 'Other' };

const mean = (values) => (values.length ? Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10 : null);

export default function StudentDiary() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isLearner = user?.role === 'learner';
  // A parent has no course player to open, so their homework rows stay as text.
  const canOpenCourses = ['learner', 'instructor', 'tenant_admin'].includes(user?.role);

  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState(params.get('student') || (isLearner ? user?.id : ''));
  const [date, setDate] = useState(params.get('date') || today());
  const [courseId, setCourseId] = useState(params.get('course') || '');
  const [courses, setCourses] = useState([]);
  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [assessing, setAssessing] = useState(null);     // entry id the form is open on
  const [editing, setEditing] = useState(null);         // assessment being edited
  const [newTopic, setNewTopic] = useState(null);       // { topic_title, content_kind, course_id }
  const [hwPaneOpen, setHwPaneOpen] = useState(false);
  const [teacherRemarks, setTeacherRemarks] = useState('');
  const [parentRemarks, setParentRemarks] = useState('');
  const [message, setMessage] = useState('');

  useTabLabel('Student diary');

  // --- who can we open
  useEffect(() => {
    let live = true;
    api.get('/diary/students')
      .then((r) => {
        if (!live) return;
        setStudents(r.data || []);
        setStudentId((cur) => cur || r.data?.[0]?.id || '');
      })
      .catch(() => setStudents([]));
    return () => { live = false; };
  }, [api]);

  useEffect(() => {
    if (!studentId) { setCourses([]); return undefined; }
    let live = true;
    api.get(`/diary/courses?student_id=${studentId}`)
      .then((r) => live && setCourses(r.data || []))
      .catch(() => live && setCourses([]));
    return () => { live = false; };
  }, [api, studentId]);

  const load = useCallback(async () => {
    if (!studentId) { setLoading(false); return; }
    setLoading(true);
    try {
      const q = new URLSearchParams({ student_id: studentId, date });
      if (courseId) q.set('course_id', courseId);
      const r = await api.get(`/diary?${q.toString()}`);
      setDay(r.data);
      setTeacherRemarks(r.data.teacher_remarks || '');
      setParentRemarks(r.data.parent_remarks || '');
    } catch (e) {
      setDay(null);
      toast.error(e.response?.data?.detail || 'Could not open the diary');
    } finally {
      setLoading(false);
    }
  }, [api, studentId, date, courseId]);
  useEffect(() => { load(); }, [load]);

  // keep the URL in step so a diary day can be linked to
  useEffect(() => {
    const next = {};
    if (studentId && !isLearner) next.student = studentId;
    if (date) next.date = date;
    if (courseId) next.course = courseId;
    setParams(next, { replace: true });
  }, [studentId, date, courseId, isLearner, setParams]);

  const can = day?.can || {};
  const entries = day?.entries || [];
  const homework = day?.homework || [];

  const tiles = useMemo(() => {
    const teacherScores = entries.flatMap((e) => (e.teacher_assessments || []).map((a) => a.accuracy)).filter((v) => typeof v === 'number');
    const parentScores = entries.flatMap((e) => (e.parent_assessments || []).map((a) => a.accuracy)).filter((v) => typeof v === 'number');
    const quizScores = entries.map((e) => e.system_assessment?.accuracy).filter((v) => typeof v === 'number');
    const open = homework.filter((h) => h.status !== 'completed').length;
    return [
      { label: 'Topics covered', value: entries.length, icon: BookOpen, testid: 'tile-topics' },
      { label: 'Teacher accuracy', value: mean([...teacherScores, ...quizScores]) === null ? '—' : `${mean([...teacherScores, ...quizScores])}%`, icon: ChalkboardTeacher, iconClass: 'bg-sky-100 text-sky-700', testid: 'tile-teacher' },
      { label: 'Parent accuracy', value: mean(parentScores) === null ? '—' : `${mean(parentScores)}%`, icon: UsersThree, iconClass: 'bg-violet-100 text-violet-700', testid: 'tile-parent' },
      { label: 'Homework open', value: open, icon: ClipboardText, iconClass: open ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700', testid: 'tile-homework' },
    ];
  }, [entries, homework]);

  // ------------------------------------------------------------------ actions
  const run = async (fn, okMessage) => {
    setBusy(true);
    try {
      await fn();
      if (okMessage) toast.success(okMessage);
      await load();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'That did not work');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveAssessment = async (entryId, payload) => {
    const ok = await run(
      () => (editing
        ? api.put(`/diary/assessments/${editing.id}`, payload)
        : api.post(`/diary/entries/${entryId}/assessments`, payload)),
      editing ? 'Assessment updated' : 'Assessment recorded'
    );
    if (ok) { setAssessing(null); setEditing(null); }
  };

  const addTopic = async () => {
    if (!newTopic?.topic_title?.trim()) { toast.error('Give the topic a name.'); return; }
    const ok = await run(() => api.post('/diary/entries', {
      student_id: studentId, date,
      topic_title: newTopic.topic_title.trim(),
      content_kind: newTopic.content_kind || 'theory',
      course_id: newTopic.course_id || courseId || null,
    }), 'Topic added');
    if (ok) setNewTopic(null);
  };

  const saveRemarks = (which) => run(
    () => api.put('/diary/remarks', { student_id: studentId, date, remarks: which === 'teacher' ? teacherRemarks : parentRemarks }),
    'Remarks saved'
  );

  const send = async () => {
    if (!message.trim()) return;
    const ok = await run(() => api.post('/diary/messages', { student_id: studentId, date, body: message.trim() }), null);
    if (ok) setMessage('');
  };

  const student = day?.student;

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="student-diary-page"
        title={isLearner ? 'My diary' : (student?.name ? `Diary — ${student.name}` : 'Student diary')}
        subtitle={`${prettyDate(date)}${courseId ? ` · ${courses.find((c) => c.id === courseId)?.title || ''}` : ' · all subjects'}`}
        onBack={null}
        onRefresh={load}
        refreshing={loading}
        loading={loading && !day}
        primary={can.add_entry ? [
          { key: 'topic', label: 'Add topic', icon: Plus, onClick: () => setNewTopic({ content_kind: 'theory', topic_title: '' }), testid: 'add-topic-btn' },
          { key: 'hw', label: 'Assign homework', icon: ClipboardText, variant: 'outline', onClick: () => setHwPaneOpen(true), testid: 'assign-hw-btn' },
        ] : []}
        side={can.assign_homework ? {
          open: hwPaneOpen,
          onOpenChange: setHwPaneOpen,
          title: 'Assign homework',
          subtitle: prettyDate(date),
          icon: ClipboardText,
          storageKey: 'diary-homework-pane',
          testid: 'assign-hw-pane',
          children: hwPaneOpen ? (
            <AssignHomework api={api} students={students} courses={courses}
              defaultStudentId={studentId} date={date}
              onDone={() => { setHwPaneOpen(false); load(); }}
              onCancel={() => setHwPaneOpen(false)} />
          ) : null,
          footer: <PaneFooter onCancel={() => setHwPaneOpen(false)} cancelLabel="Close" />,
        } : null}
      >
        {/* ------------------------------------------------- date / student / subject */}
        <FormSection title="Diary for" icon={CalendarBlank} bodyClassName="p-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="field">
              <Label htmlFor="d-date">Date</Label>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setDate(shiftDate(date, -1))} title="Previous day" data-testid="prev-day">
                  <CaretLeft className="w-4 h-4" />
                </Button>
                <Input id="d-date" type="date" value={date} onChange={(e) => setDate(e.target.value || today())}
                  className="w-[150px]" data-testid="diary-date" />
                <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setDate(shiftDate(date, 1))} title="Next day" data-testid="next-day">
                  <CaretRight className="w-4 h-4" />
                </Button>
                {date !== today() && (
                  <Button variant="ghost" size="sm" className="h-8" onClick={() => setDate(today())} data-testid="today-btn">Today</Button>
                )}
              </div>
            </div>

            {!isLearner && (
              <div className="field">
                <Label htmlFor="d-student">Student</Label>
                <select id="d-student" className="form-select w-[220px]" value={studentId}
                  onChange={(e) => setStudentId(e.target.value)} data-testid="diary-student">
                  {students.length === 0 && <option value="">No students</option>}
                  {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}

            <div className="field">
              <Label htmlFor="d-course">Subject</Label>
              <select id="d-course" className="form-select w-[220px]" value={courseId}
                onChange={(e) => setCourseId(e.target.value)} data-testid="diary-subject">
                <option value="">All subjects</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
          </div>
        </FormSection>

        <StatTiles items={tiles} />

        <div className="grid gap-3 lg:grid-cols-5 items-start">
          {/* ------------------------------------------------- topics + assessments */}
          <div className="lg:col-span-3 space-y-3">
            <FormSection title="Topics covered" icon={BookOpen} bodyClassName="p-3 space-y-3">
              {newTopic && (
                <div className="rounded-md border border-shell-primary/30 bg-shell-primary/5 p-2.5 space-y-2" data-testid="new-topic-form">
                  <div className="grid gap-2 sm:grid-cols-3">
                    <div className="field sm:col-span-2">
                      <Label htmlFor="t-title">Topic</Label>
                      <Input id="t-title" value={newTopic.topic_title} autoFocus
                        placeholder="e.g. Quadratic equations — oral Q&A"
                        onChange={(e) => setNewTopic({ ...newTopic, topic_title: e.target.value })}
                        data-testid="new-topic-title" />
                    </div>
                    <div className="field">
                      <Label htmlFor="t-kind">Kind</Label>
                      <select id="t-kind" className="form-select" value={newTopic.content_kind}
                        onChange={(e) => setNewTopic({ ...newTopic, content_kind: e.target.value })} data-testid="new-topic-kind">
                        {Object.entries(KIND_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                  </div>
                  <p className="field-hint">For something taught away from the LMS. Lessons and quizzes the student finishes appear here on their own.</p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={addTopic} disabled={busy} data-testid="save-topic"><FloppyDisk className="w-4 h-4" /> Add</Button>
                    <Button size="sm" variant="outline" onClick={() => setNewTopic(null)}>Cancel</Button>
                  </div>
                </div>
              )}

              {entries.length === 0 && !newTopic && (
                <p className="text-[13px] text-muted-foreground py-6 text-center" data-testid="no-topics">
                  Nothing recorded for {prettyDate(date)} yet.
                  {can.add_entry ? ' Lessons and quizzes appear automatically — use “Add topic” for anything taught offline.' : ''}
                </p>
              )}

              {entries.map((entry) => (
                <article key={entry.id} className="rounded-lg border border-border" data-testid={`diary-entry-${entry.id}`}>
                  <header className="flex items-center gap-2 px-3 h-9 border-b border-border bg-muted/30 flex-wrap">
                    <span className="rounded px-1.5 h-5 inline-flex items-center text-[10px] font-semibold uppercase bg-slate-200 text-slate-700">
                      {KIND_LABEL[entry.content_kind] || entry.content_kind}
                    </span>
                    <h4 className="text-[13px] font-semibold mr-auto truncate" data-find={entry.topic_title}>{entry.topic_title}</h4>
                    {entry.course_title && <span className="text-[11px] text-muted-foreground truncate max-w-[150px]">{entry.course_title}</span>}
                    {entry.source === 'lms'
                      ? <span className="text-[10px] uppercase text-muted-foreground">from the LMS</span>
                      : can.add_entry && (
                        <Button variant="ghost" size="sm" className="h-6 px-1.5 text-red-700"
                          onClick={() => run(() => api.delete(`/diary/entries/${entry.id}`), 'Topic removed')}
                          title="Remove this topic" data-testid={`delete-entry-${entry.id}`}>
                          <Trash className="w-3.5 h-3.5" />
                        </Button>
                      )}
                  </header>

                  <div className="p-2.5 space-y-2">
                    {entry.system_assessment && <AssessmentRecord record={entry.system_assessment} canEdit={false} />}
                    {(entry.teacher_assessments || []).map((a) => (
                      <AssessmentRecord key={a.id} record={a}
                        canEdit={can.assess_as === 'instructor' && a.assessor_id === user?.id}
                        onEdit={(r) => { setEditing(r); setAssessing(entry.id); }}
                        onDelete={(r) => run(() => api.delete(`/diary/assessments/${r.id}`), 'Assessment removed')} />
                    ))}
                    {(entry.parent_assessments || []).map((a) => (
                      <AssessmentRecord key={a.id} record={a}
                        canEdit={can.assess_as === 'parent' && a.assessor_id === user?.id}
                        onEdit={(r) => { setEditing(r); setAssessing(entry.id); }}
                        onDelete={(r) => run(() => api.delete(`/diary/assessments/${r.id}`), 'Assessment removed')} />
                    ))}

                    {!entry.system_assessment && !(entry.teacher_assessments || []).length && !(entry.parent_assessments || []).length && assessing !== entry.id && (
                      <p className="text-[12px] text-muted-foreground">Not assessed yet.</p>
                    )}

                    {assessing === entry.id ? (
                      <AssessmentForm
                        key={editing?.id || 'new'}
                        initial={editing ? {
                          method: editing.method,
                          questions_asked: editing.questions_asked ?? '',
                          correct: editing.correct ?? '',
                          partial: editing.partial ?? '',
                          incorrect: editing.incorrect ?? '',
                          understanding_score: editing.understanding_score ?? '',
                          remarks: editing.remarks || '',
                        } : null}
                        asRole={can.assess_as}
                        saving={busy}
                        onSave={(payload) => saveAssessment(entry.id, payload)}
                        onCancel={() => { setAssessing(null); setEditing(null); }}
                      />
                    ) : can.assess && (
                      <Button variant="outline" size="sm" className="h-7"
                        onClick={() => { setEditing(null); setAssessing(entry.id); }}
                        data-testid={`assess-${entry.id}`}>
                        <Target className="w-3.5 h-3.5" /> {can.assess_as === 'parent' ? 'Add my assessment' : 'Assess this topic'}
                      </Button>
                    )}
                  </div>
                </article>
              ))}
            </FormSection>
          </div>

          {/* ------------------------------------------------- homework, remarks, messages */}
          <div className="lg:col-span-2 space-y-3">
            <FormSection title="Homework" icon={ClipboardText} bodyClassName="p-3 space-y-2">
              {homework.length === 0 && <p className="text-[12px] text-muted-foreground" data-testid="no-homework">No homework set.</p>}

              {homework.map((h) => {
                // Homework set on a topic should take you to that topic. A typed task
                // with no content behind it has nothing to open, so it stays plain text
                // and shows what to do underneath instead.
                const target = homeworkTarget(h);
                const canOpen = !!target && canOpenCourses;
                return (
                <div key={h.id} className="rounded-md border border-border p-2.5 space-y-1" data-testid={`homework-${h.id}`}>
                  <div className="flex items-start gap-2">
                    {canOpen ? (
                      <button
                        type="button"
                        onClick={() => navigate(target)}
                        className="text-[13px] font-medium mr-auto text-left text-shell-primary hover:underline inline-flex items-start gap-1 min-w-0"
                        title={h.exam_id ? 'Open this quiz' : 'Open this lesson'}
                        data-testid={`open-hw-${h.id}`}
                      >
                        <span className="min-w-0">{h.title}</span>
                        <ArrowSquareOut className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 opacity-70" />
                      </button>
                    ) : (
                      <span className="text-[13px] font-medium mr-auto">{h.title}</span>
                    )}
                    {h.status === 'completed'
                      ? <span className="inline-flex items-center gap-1 rounded px-1.5 h-5 text-[10px] font-semibold uppercase bg-emerald-100 text-emerald-800"><CheckCircle className="w-3 h-3" weight="fill" />Done</span>
                      : <span className={`inline-flex items-center gap-1 rounded px-1.5 h-5 text-[10px] font-semibold uppercase ${h.overdue ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                          <Clock className="w-3 h-3" />{h.overdue ? 'Overdue' : 'To do'}
                        </span>}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {h.course_title ? `${h.course_title} · ` : ''}{h.due_date ? `due ${prettyDate(h.due_date)}` : 'no due date'}
                    {h.assigned_by_name ? ` · set by ${h.assigned_by_name}` : ''}
                  </p>
                  {/* What the teacher actually asked for. It was being stored and never
                      shown, which left "Notes" as the whole of the homework. */}
                  {h.instructions && (
                    <RichHtml html={h.instructions} className="text-[12px] text-foreground/90"
                      data-testid={`hw-instructions-${h.id}`} />
                  )}
                  {h.status === 'completed' && h.completed_by && (
                    <p className="text-[11px] text-emerald-700">Marked done by {h.completed_by} · {when(h.completed_at)}</p>
                  )}
                  {can.complete_homework && (
                    <Button variant="outline" size="sm" className="h-6 text-[11px]" disabled={busy}
                      onClick={() => run(() => api.put(`/diary/homework/${h.id}`, { status: h.status === 'completed' ? 'assigned' : 'completed' }),
                        h.status === 'completed' ? 'Reopened' : 'Marked complete')}
                      data-testid={`toggle-hw-${h.id}`}>
                      {h.status === 'completed' ? 'Reopen' : 'Mark complete'}
                    </Button>
                  )}
                </div>
                );
              })}
            </FormSection>

            <FormSection title="Teacher remarks" icon={ChalkboardTeacher} bodyClassName="p-3 space-y-2">
              {can.teacher_remarks ? (
                <>
                  <Textarea rows={3} value={teacherRemarks} onChange={(e) => setTeacherRemarks(e.target.value)}
                    placeholder="Observations about learning or behaviour…" data-testid="teacher-remarks" />
                  <Button size="sm" onClick={() => saveRemarks('teacher')} disabled={busy} data-testid="save-teacher-remarks">
                    <FloppyDisk className="w-4 h-4" /> Save remarks
                  </Button>
                </>
              ) : (
                <p className="text-[13px] whitespace-pre-wrap" data-testid="teacher-remarks-read">
                  {day?.teacher_remarks || <span className="text-muted-foreground">Nothing from the teacher yet.</span>}
                </p>
              )}
              {day?.teacher_remarks_by && (
                <p className="text-[11px] text-muted-foreground">{day.teacher_remarks_by} · {when(day.teacher_remarks_at)}</p>
              )}
            </FormSection>

            <FormSection title="Parent assessment & remarks" icon={UsersThree} bodyClassName="p-3 space-y-2">
              {can.parent_remarks ? (
                <>
                  <Textarea rows={3} value={parentRemarks} onChange={(e) => setParentRemarks(e.target.value)}
                    placeholder="How it went at home…" data-testid="parent-remarks" />
                  <Button size="sm" onClick={() => saveRemarks('parent')} disabled={busy} data-testid="save-parent-remarks">
                    <FloppyDisk className="w-4 h-4" /> Save remarks
                  </Button>
                  <p className="field-hint">To record how accurately your child answered a topic, use “Add my assessment” on that topic.</p>
                </>
              ) : (
                <p className="text-[13px] whitespace-pre-wrap" data-testid="parent-remarks-read">
                  {day?.parent_remarks || <span className="text-muted-foreground">Nothing from the parent yet.</span>}
                </p>
              )}
              {day?.parent_remarks_by && (
                <p className="text-[11px] text-muted-foreground">{day.parent_remarks_by} · {when(day.parent_remarks_at)}</p>
              )}
            </FormSection>

            <FormSection title="Parent ↔ teacher" icon={ChatsCircle} bodyClassName="p-3 space-y-2">
              <div className="space-y-2 max-h-[280px] overflow-y-auto" data-testid="diary-messages">
                {(day?.messages || []).length === 0 && <p className="text-[12px] text-muted-foreground">No messages yet.</p>}
                {(day?.messages || []).map((m) => (
                  <div key={m.id} className={`rounded-md p-2 ${m.author_role === 'parent' ? 'bg-violet-50' : 'bg-sky-50'}`} data-testid={`message-${m.author_role}`}>
                    <p className="text-[11px] text-muted-foreground">
                      {m.author_name} · {m.author_role === 'parent' ? 'Parent' : 'Teacher'} · {when(m.created_at)}
                    </p>
                    <p className="text-[13px] whitespace-pre-wrap">{m.body}</p>
                  </div>
                ))}
              </div>
              {can.message && (
                <div className="flex gap-2">
                  <Input value={message} onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Write a message…" data-testid="message-input" />
                  <Button size="sm" onClick={send} disabled={busy || !message.trim()} data-testid="send-message">
                    <PaperPlaneTilt className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </FormSection>
          </div>
        </div>
      </RecordPage>
    </DashboardLayout>
  );
}
